import React, { useState } from 'react';
import {
    Settings,
    Send,
    UploadCloud,
    Plus,
    Tag,
    Globe,
    Mail,
    CheckCircle2,
    Trash2,
    RefreshCw,
} from 'lucide-react';

export default function PublishingHub({
    contentType,
    setContentType,
    featuredImage,
    setFeaturedImage,
    availableCategories,
    setAvailableCategories,
    selectedCategories,
    setSelectedCategories,
    publishStatus,
    setPublishStatus,
    isPublishing,
    publishSuccess,
    onPublish,
    onSaveDraft,
}) {
    // UI-only state for the "Add Category" input
    const [newCategoryInput, setNewCategoryInput] = useState('');
    const [showCategoryInput, setShowCategoryInput] = useState(false);


    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => setFeaturedImage(reader.result);
        reader.readAsDataURL(file);
    };

    return (
        <aside className="w-80 bg-white border-l border-slate-200 p-6 flex flex-col justify-between overflow-y-auto shrink-0 shadow-lg custom-scrollbar">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                    <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                        <Settings className="w-4 h-4 text-blue-500" />
                        Publishing Hub
                    </h3>
                </div>

                {/* Content destination */}
                <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        Content Destination
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            onClick={() => setContentType('blog')}
                            className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-semibold transition-all ${contentType === 'blog'
                                    ? 'border-sky-500 bg-sky-500 text-white shadow-sm'
                                    : 'bg-white border-slate-200 text-black hover:bg-slate-50'
                                }`}
                        >
                            <Globe className="w-4 h-4" />
                            <span>Blog Post</span>
                        </button>

                        <button
                            onClick={() => setContentType('newsletter')}
                            className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-semibold transition-all ${contentType === 'newsletter'
                                ? 'border-sky-500 bg-sky-500 text-white shadow-sm'
                                : 'bg-white border-slate-200 text-black hover:bg-slate-50'
                                }`}
                        >
                            <Mail className="w-4 h-4" />
                            <span>Newsletter</span>
                        </button>
                    </div>
                </div>

                {/* Featured image */}
                <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        Featured Header Image
                    </label>
                    {featuredImage ? (
                        <div className="relative group rounded-2xl overflow-hidden border border-slate-200">
                            {/* Increased height from h-32 to h-48 */}
                            <img src={featuredImage} alt="Featured" className="w-full h-48 object-cover" />
                            <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                                <button
                                    onClick={() => setFeaturedImage('')}
                                    className="p-1.5 bg-red-600 text-white rounded-lg hover:bg-red-500 transition"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Increased padding from p-4 to p-8 */
                        <label className="border-2 border-dashed border-slate-300 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition bg-slate-50">
                            <UploadCloud className="w-10 h-10 text-slate-400 mb-2" />
                            <span className="text-xs font-semibold text-slate-600">
                                Click or Drag Image
                            </span>
                            <span className="text-[10px] text-slate-400 mt-0.5">PNG, JPG up to 5MB</span>
                            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                        </label>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div className="pt-6 border-t border-slate-100 space-y-2">
                {publishSuccess && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-xs font-semibold flex items-center gap-2 animate-bounce">
                        <CheckCircle2 className="w-4 h-4 shrink-0" />
                        <span>Published to WordPress website successfully!</span>
                    </div>
                )}

                <button
                    onClick={onPublish}
                    disabled={isPublishing}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-xs font-extrabold shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 transition-all disabled:opacity-50 active:scale-95"
                >
                    {isPublishing ? (
                        <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Syncing with WordPress...</span>
                        </>
                    ) : (
                        <>
                            <Send className="w-4 h-4" />
                            <span>Publish to Website</span>
                        </>
                    )}
                </button>

                <button
                    onClick={onSaveDraft}
                    className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition"
                >
                    Save Draft
                </button>
            </div>
        </aside>
    );
}